import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, filter, finalize, map, switchMap, take } from "rxjs/operators";
import { storedCustomerData, storedValudationData, urlAuth } from "../../models/urlAuthParams.model";
import { AuthenticationService } from '../authentication.service';
import { authModel, storageApiKey } from '../models/auth';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(public authenticationService: AuthenticationService, private readonly route: ActivatedRoute) { }
    public isRefreshing = false;
    public tokenSubject: BehaviorSubject<authModel> = new BehaviorSubject(null);
    public tokenReq: any;

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        const re = /login|oauth|authentication/gi;

        // Check if we want to login
        if (request.url.search(re) !== -1) {
            return next.handle(request)
        }

        if (localStorage.getItem("auth")) {
            return of(JSON.parse(localStorage.getItem("auth"))).pipe(
                map((currentUser: authModel) => {
                    if (this.authenticationService.checkAccessTokenValidity() == false) {
                        throw (new HttpErrorResponse({ status: 401 }))
                    }
                    return currentUser.access_token
                }),
                switchMap((token) => {
                    if (this.isRefreshing == false) {
                        const tokenReq = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${token}`
                            }
                        });
                        return next.handle(tokenReq)
                    }
                }),
                catchError(err => {
                    if (err instanceof HttpErrorResponse) {
                        // token is expired refresh and try again
                        if (err.status === 401) {
                            return this.handleUnauthorized(request, next);
                        }
                    } else {
                        return throwError(err);
                    }
                })
            );
        } else {
            // No login data yet.
            return this.handleUnauthorized(request, next);
        }
    }

    public handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): any {
        if (!this.isRefreshing) {
            const currentUser: authModel = JSON.parse(localStorage.getItem("auth"));
            this.isRefreshing = true;
            this.tokenSubject.next(null);

            // Refresh token is valid, use this to extend the access token.
            if (this.authenticationService.checkRefreshTokenValidity() == true) {
                // Api refresh token is valid, use it.
                return this.authenticationService.loginWithRefreshToken(currentUser.refresh_token)
                    .pipe(
                        switchMap((newAuthModel: authModel) => {
                            const newToken = newAuthModel.access_token;
                            if (newToken) {
                                this.tokenSubject.next(newAuthModel);
                                const tokenReq = request.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newToken}`
                                    }
                                });
                                return next.handle(tokenReq);
                            }
                            this.doLogout();
                        }),
                        catchError(error => {
                            this.doLogout();
                            return error;
                        }),
                        finalize(() => {
                            this.isRefreshing = false;
                        })
                    );
            } else if (this.authenticationService.checkApiKeyValidity() == true) {
                // Api key is valid, use it.
                const currentUser = JSON.parse(localStorage.getItem('auth'));
                const apiKey: storageApiKey = JSON.parse(localStorage.getItem('apiKey'));
                return this.authenticationService.loginWithApiKey(apiKey.api_key, currentUser.username, currentUser.device_token)
                    .pipe(
                        switchMap((newAuthModel: authModel) => {
                            const newToken = newAuthModel.access_token;
                            if (newToken) {
                                this.tokenSubject.next(newAuthModel);
                                const tokenReq = request.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newToken}`
                                    }
                                });
                                return next.handle(tokenReq);
                            }
                            this.doLogout();
                        }),
                        catchError(error => {
                            this.doLogout();
                            return error
                        }),
                        finalize(() => {
                            this.authenticationService.requestApiKey(currentUser.device_token).subscribe(data => {
                                this.isRefreshing = false;
                            })
                        })
                    );
            } else {
                // No keys are valid or set. request new token.
                const customer: storedCustomerData = JSON.parse(localStorage.getItem('customer'));
                return this.authenticationService.login(String(customer.id), customer.hash, this.authenticationService.generateDeviceToken(24))
                    .pipe(
                        switchMap((newAuthModel: authModel) => {
                            const newToken = newAuthModel.access_token;
                            if (newToken) {
                                this.tokenSubject.next(newAuthModel);
                                const tokenReq = request.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newToken}`
                                    }
                                });
                                return next.handle(tokenReq);
                            }
                            this.doLogout();
                        }),
                        catchError(error => {
                            this.doLogout();
                            return error;
                        }),
                        finalize(() => {
                            this.isRefreshing = false;
                        })
                    );
            }
        } else {
            return this.tokenSubject.pipe(
                filter((authModel: authModel) => authModel != null),
                take(1),
                switchMap(authModel => {
                    const token = authModel.access_token;
                    const tokenReq = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    return next.handle(tokenReq);
                })
            );
        }
    }

    public doLogout(): void {
        this.authenticationService.logout();
    }
}
